import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { getSafeLocalStorage, isRestrictedWebView } from "@/lib/safeStorage";
import { UserProfileProvider, useUserProfile } from "./contexts/UserProfileContext";
import { OrganizationProvider } from "./contexts/OrganizationContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { useTaskAutoGeneration } from "./hooks/useTaskAutoGeneration";
import { useAnalytics } from "./hooks/useAnalytics";
import { LoadingScreen } from "./components/shared/LoadingScreen";
import { TourGuide } from "./components/onboarding/TourGuide";
import { OfflineBanner } from "./components/shared/OfflineBanner";
import { CookieConsent } from "./components/gdpr/CookieConsent";
import { AccessDenied } from "./components/shared/AccessDenied";
import { VisibilityHandler } from "./components/shared/VisibilityHandler";
import { InstallPrompt } from "./components/pwa/InstallPrompt";
import { UpdatePrompt } from "./components/pwa/UpdatePrompt";
import { PWAProvider } from "./contexts/PWAContext";
import { useMigrationVersion } from "./hooks/useMigrationVersion";
import { useAppReleaseCheck } from "./hooks/useAppReleaseCheck";
import { LandingLanguageProvider } from "./hooks/useLandingLanguage";

// Runs the migration-version check once on mount and force-reloads
// any client that is still on a pre-refactor bundle (e.g. legacy
// per-project Globe domain wizard). Mounted inside <PWAProvider>
// so it can leverage the registered service worker.
const MigrationGate = () => {
  useMigrationVersion();
  useAppReleaseCheck();
  return null;
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const sectionId = decodeURIComponent(hash.slice(1));
      window.requestAnimationFrame(() => {
        document.getElementById(sectionId)?.scrollIntoView({ block: "start" });
      });
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
};

// Eager load critical pages
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";

// Lazy load all other pages
const Pricing = lazy(() => import("./pages/Pricing"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Concerts = lazy(() => import("./pages/Concerts"));
const Contacts = lazy(() => import("./pages/Contacts"));
const Venues = lazy(() => import("./pages/Venues"));
const Tasks = lazy(() => import("./pages/Tasks"));
const Expenses = lazy(() => import("./pages/Expenses"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Profile = lazy(() => import("./pages/Profile"));
const Settings = lazy(() => import("./pages/Settings"));
const ImportConcerts = lazy(() => import("./pages/admin/ImportConcerts"));
const PaymentAnalytics = lazy(() => import("./pages/admin/PaymentAnalytics"));
const LandingImages = lazy(() => import("./pages/admin/LandingImages"));
const LandingSettings = lazy(() => import("./pages/admin/LandingSettings"));
const ContractTemplates = lazy(() => import("./pages/ContractTemplates"));
const Contracts = lazy(() => import("./pages/Contracts"));
const MyFees = lazy(() => import("./pages/MyFees"));
const CostCalculator = lazy(() => import("./pages/CostCalculator"));
const TourCalculator = lazy(() => import("./pages/TourCalculator"));
const MediaLibrary = lazy(() => import("./pages/MediaLibrary"));
const ContentArchive = lazy(() => import("./pages/ContentArchive"));
const RevenueShares = lazy(() => import("./pages/RevenueShares"));
const Calendar = lazy(() => import("./pages/Calendar"));
const ArtisticProjects = lazy(() => import("./pages/ArtisticProjects"));
const EmailTest = lazy(() => import("./pages/EmailTest"));
const EmailLogs = lazy(() => import("./pages/EmailLogs"));
const Catalog = lazy(() => import("./pages/Catalog"));
const AnnualPlan = lazy(() => import("./pages/AnnualPlan"));
const Repertoire = lazy(() => import("./pages/Repertoire"));

const AIExpert = lazy(() => import("./pages/AIExpert"));
const Tutorials = lazy(() => import("./pages/Tutorials"));
const Fans = lazy(() => import("./pages/Fans"));
const FanDataQuality = lazy(() => import("./pages/FanDataQuality"));
const FanAnalytics = lazy(() => import("./pages/FanAnalytics"));
const Products = lazy(() => import("./pages/Products"));
const Integrations = lazy(() => import("./pages/Integrations"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const CookiePage = lazy(() => import("./pages/Cookie"));
const GDPR = lazy(() => import("./pages/GDPR"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const Careers = lazy(() => import("./pages/Careers"));
const Press = lazy(() => import("./pages/Press"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Docs = lazy(() => import("./pages/Docs"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const NotFound = lazy(() => import("./pages/NotFound"));
const OrganizationManagement = lazy(() => import("./pages/OrganizationManagement"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ConfirmEmail = lazy(() => import("./pages/ConfirmEmail"));
const AuthError = lazy(() => import("./pages/AuthError"));
const AuthConfirm = lazy(() => import("./pages/AuthConfirm"));
const FanProfile = lazy(() => import("./pages/FanProfile"));
const PublicFanProfileRequest = lazy(() => import("./pages/PublicFanProfileRequest"));
const PublicCatalog = lazy(() => import("./pages/PublicCatalog"));
const CMSPages = lazy(() => import("./pages/admin/CMSPages"));
const CMSPage = lazy(() => import("./pages/CMSPage"));
const BlogAdmin = lazy(() => import("./pages/admin/BlogAdmin"));
const PlatformUpdates = lazy(() => import("./pages/admin/PlatformUpdates"));
const AutoCommunications = lazy(() => import("./pages/admin/AutoCommunications"));
const UpgradePush = lazy(() => import("./pages/admin/UpgradePush"));
const PlatformUsers = lazy(() => import("./pages/admin/PlatformUsers"));
const BlueprintPage = lazy(() => import("./pages/Blueprint"));
const SystemHealth = lazy(() => import("./pages/admin/SystemHealth"));

// React Query cache configuration constants
export const QUERY_CACHE_TIMES = {
  // Static data - rarely changes (venues, contacts, venue types)
  static: 30 * 60 * 1000, // 30 minutes
  // Semi-static data - changes occasionally (concerts, tasks)
  frequent: 5 * 60 * 1000, // 5 minutes
  // Analytics - expensive to compute, can be stale
  analytics: 15 * 60 * 1000, // 15 minutes
  // Real-time data - should be fresh (notifications)
  realtime: 0,
  // Dashboard - moderate freshness needed
  dashboard: 2 * 60 * 1000, // 2 minutes
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: QUERY_CACHE_TIMES.frequent, // 5 minutes default
      gcTime: 24 * 60 * 60 * 1000, // 24 hours for persistence
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
    mutations: {
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
  },
});

// Create persister for localStorage cache persistence — falls back to a
// safe in-memory storage if the browser blocks localStorage (WhatsApp/IG
// in-app webviews on Android).
const persister = createSyncStoragePersister({
  storage: getSafeLocalStorage(),
  key: 'MAESTRO_CRM_CACHE',
});
const restrictedWebView = isRestrictedWebView();

type AppRole = 'artist' | 'booking_manager' | 'venue_manager' | 'accountant' | 'admin';

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, userRoles } = useUserProfile();
  const location = useLocation();
  
  // Track when the user identity first appears so we can give the
  // profile loader a short grace period before redirecting to /onboarding.
  // This prevents the Android/PWA flicker where /dashboard mounts faster
  // than fetchProfile resolves the user's roles.
  const userArrivedAt = useRef<number | null>(null);
  const [graceElapsed, setGraceElapsed] = useState(false);
  
  useEffect(() => {
    if (user?.id && userArrivedAt.current === null) {
      userArrivedAt.current = Date.now();
      setGraceElapsed(false);
      const t = setTimeout(() => setGraceElapsed(true), 1200);
      return () => clearTimeout(t);
    }
    if (!user?.id) {
      userArrivedAt.current = null;
      setGraceElapsed(false);
    }
  }, [user?.id]);
  
  // Auto-generate tasks for authenticated users
  useTaskAutoGeneration();
  
  // Track page views with Google Analytics
  useAnalytics();
  
  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Redirect to onboarding if user has no roles — but only after the grace
  // period to avoid bouncing right after sign-in / OAuth on slow networks.
  if (userRoles.length === 0 && location.pathname !== '/onboarding') {
    if (!graceElapsed) {
      return (
        <div className="min-h-dvh flex items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      );
    }
    return <Navigate to="/onboarding" replace />;
  }
  
  return <>{children}</>;
};

// Role-based route protection component
const RoleProtectedRoute = ({ 
  children, 
  allowedRoles 
}: { 
  children: React.ReactNode; 
  allowedRoles?: AppRole[];
}) => {
  const { userRoles, isAdmin, isLoading } = useUserProfile();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  // Admin always has access to everything
  const isReallyAdmin = isAdmin === true && userRoles?.includes('admin') === true;
  if (isReallyAdmin) return <>{children}</>;
  
  // If no roles required, everyone has access
  if (!allowedRoles || allowedRoles.length === 0) return <>{children}</>;
  
  // Check if user has any of the required roles
  const hasAccess = allowedRoles.some(role => userRoles?.includes(role));
  
  if (!hasAccess) {
    return <AccessDenied />;
  }
  
  return <>{children}</>;
};

const AppProviders = ({ children }: { children: React.ReactNode }) =>
  restrictedWebView ? (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  ) : (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 24 * 60 * 60 * 1000, // 24 hours
        buster: import.meta.env.VITE_BUILD_VERSION || Date.now().toString(),
      }}
    >
      {children}
    </PersistQueryClientProvider>
  );

const App = () => (
  <AppProviders>
    <UserProfileProvider>
      <OrganizationProvider>
        <LanguageProvider>
          <TooltipProvider>
          <PWAProvider>
          <MigrationGate />
          <OfflineBanner />
          <CookieConsent />
          <VisibilityHandler />
          <InstallPrompt />
          <UpdatePrompt />
          </PWAProvider>
          <Toaster />
          <Sonner />
          <LandingLanguageProvider>
            <BrowserRouter>
              <ScrollToTop />
              <TourGuide />
              <Suspense fallback={<LoadingScreen />}>
                <Routes>
                {/* Public routes */}
                <Route path="/" element={<Landing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/cookie" element={<CookiePage />} />
                <Route path="/gdpr" element={<GDPR />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/roadmap" element={<Roadmap />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/press" element={<Press />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/docs" element={<Docs />} />
                <Route path="/invite/:token" element={<AcceptInvite />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/confirm-email" element={<ConfirmEmail />} />
                <Route path="/auth-error" element={<AuthError />} />
                <Route path="/auth/confirm" element={<AuthConfirm />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                <Route path="/fan/profile" element={<FanProfile />} />
                <Route path="/p/:slug/aggiorna-profilo" element={<PublicFanProfileRequest />} />
                <Route path="/p/:slug/update-profile" element={<PublicFanProfileRequest />} />
                <Route path="/c/:token" element={<PublicCatalog />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
                
                {/* Core routes - all authenticated users */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/concerts" element={<ProtectedRoute><Concerts /></ProtectedRoute>} />
                <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
                <Route path="/artistic-projects" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['booking_manager', 'admin']}>
                      <ArtisticProjects />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/email-test" element={<ProtectedRoute><EmailTest /></ProtectedRoute>} />
                <Route path="/email-logs" element={<ProtectedRoute><EmailLogs /></ProtectedRoute>} />
                <Route path="/catalog" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist', 'booking_manager', 'admin']}>
                      <Catalog />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/annual-plan" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist', 'booking_manager', 'admin']}>
                      <AnnualPlan />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/repertoire" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist', 'booking_manager', 'admin']}>
                      <Repertoire />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                
                {/* Management routes - role restricted */}
                <Route path="/contacts" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist', 'booking_manager', 'admin']}>
                      <Contacts />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/venues" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['booking_manager', 'venue_manager', 'admin']}>
                      <Venues />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/tasks" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist', 'booking_manager', 'venue_manager', 'admin']}>
                      <Tasks />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/contracts" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['booking_manager', 'venue_manager', 'admin']}>
                      <Contracts />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/contracts/templates" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['booking_manager', 'venue_manager', 'admin']}>
                      <ContractTemplates />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                
                {/* Fan & Marketing routes - artist only */}
                <Route path="/fans" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist']}>
                      <Fans />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/fans/segments" element={<Navigate to="/fans?tab=segments" replace />} />
                <Route path="/fans/automations" element={<Navigate to="/fans?tab=automations" replace />} />
                <Route path="/fans/purchase-intel" element={<Navigate to="/fans?tab=purchase-intel" replace />} />
                <Route path="/fans/analytics" element={<Navigate to="/fans?tab=analytics" replace />} />
                <Route path="/fans/data-quality" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist']}>
                      <FanDataQuality />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/fan-analytics" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist']}>
                      <FanAnalytics />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/integrations" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist', 'booking_manager', 'admin']}>
                      <Integrations />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/products" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist', 'booking_manager', 'admin']}>
                      <Products />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                
                {/* Financial routes - role restricted */}
                <Route path="/revenue-shares" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist', 'booking_manager', 'accountant', 'admin']}>
                      <RevenueShares />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/expenses" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist', 'booking_manager', 'accountant', 'admin']}>
                      <Expenses />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/my-fees" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist', 'booking_manager']}>
                      <MyFees />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/cost-calculator" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['booking_manager', 'admin']}>
                      <CostCalculator />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/tour-calculator" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['booking_manager', 'admin']}>
                      <TourCalculator />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/analytics" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist', 'booking_manager', 'accountant', 'admin']}>
                      <Analytics />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                
                {/* Tools routes - various restrictions */}
                <Route path="/media-library" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist', 'booking_manager', 'admin']}>
                      <MediaLibrary />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/content-archive" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['artist', 'booking_manager', 'admin']}>
                      <ContentArchive />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/ai-expert" element={<ProtectedRoute><AIExpert /></ProtectedRoute>} />
                <Route path="/tutorials" element={<ProtectedRoute><Tutorials /></ProtectedRoute>} />
                <Route path="/help" element={<Navigate to="/tutorials" replace />} />
                
                {/* Admin only routes */}
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['admin']}>
                      <Settings />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/import-concerts" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['admin']}>
                      <ImportConcerts />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/payment-analytics" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['admin']}>
                      <PaymentAnalytics />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/landing-images" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['admin']}>
                      <LandingImages />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/landing-settings" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['admin']}>
                      <LandingSettings />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/cms" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['admin']}>
                      <CMSPages />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/blog" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['admin']}>
                      <BlogAdmin />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/platform-updates" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['admin']}>
                      <PlatformUpdates />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/auto-communications" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['admin']}>
                      <AutoCommunications />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/upgrade-push" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['admin']}>
                      <UpgradePush />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/users" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['admin']}>
                      <PlatformUsers />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/organization" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['admin']}>
                      <OrganizationManagement />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/blueprint" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['admin']}>
                      <BlueprintPage />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/system-health" element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['admin']}>
                      <SystemHealth />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                } />
                
                {/* CMS Dynamic Pages */}
                <Route path="/p/:slug" element={<CMSPage />} />
                
                {/* 404 */}
                <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </LandingLanguageProvider>
        </TooltipProvider>
      </LanguageProvider>
    </OrganizationProvider>
    </UserProfileProvider>
  </AppProviders>

);

export default App;